"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { useWatch } from "react-hook-form";
import { format } from "date-fns";
import { MapPin, Calendar as CalendarIcon, Users, Search, Car, ShieldCheck, Minus, Plus, Mail, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const PassengerStepper = ({
  label,
  value,
  onChange,
  min = 0,
  max = 10
}: {
  label: string;
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
}) => {
  return <div className="flex items-center justify-between py-3 border-b border-border/50 last:border-0">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <div className="flex items-center gap-4">
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-lg border-primary/20 text-primary hover:bg-primary/10" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>
          <Minus className="size-3" />
        </Button>
        <span className="w-4 text-center text-sm font-bold">{value}</span>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-lg border-primary/20 text-primary hover:bg-primary/10" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}>
          <Plus className="size-3" />
        </Button>
      </div>
    </div>;
};
function BookingFormContent() {
  const {
    control,
    isSubmitting,
    handleSubmit
  } = useWvcForm();
  const [serviceType, setServiceType] = useState("sedan");
  const adults = useWatch({
    control,
    name: "adults"
  }) || 1;
  const childrenCount = useWatch({
    control,
    name: "children"
  }) || 0;
  const totalPassengers = (Number(adults) || 0) + (Number(childrenCount) || 0);
  return <div className="relative">
      <div className="mb-8 flex flex-col items-center gap-4 md:flex-row md:justify-between">
        <ToggleGroup type="single" value={serviceType} onValueChange={val => val && setServiceType(val)} className="bg-white/10 p-1 backdrop-blur-md rounded-lg border border-white/20 shadow-soft">
          <ToggleGroupItem value="sedan" className="rounded-md px-6 py-2 data-[state=on]:bg-primary data-[state=on]:text-white transition-all font-semibold text-xs uppercase tracking-wider text-white/70">
            <Car className="mr-2 size-4" />
            Luxury Sedan
          </ToggleGroupItem>
          <ToggleGroupItem value="suv" className="rounded-md px-6 py-2 data-[state=on]:bg-primary data-[state=on]:text-white transition-all font-semibold text-xs uppercase tracking-wider text-white/70">
            <ShieldCheck className="mr-2 size-4" />
            Premium SUV
          </ToggleGroupItem>
        </ToggleGroup>
        
        <div className="hidden lg:flex items-center gap-2 text-white/60 text-sm font-medium">
          <Clock className="size-4 text-secondary" />
          <span>24/7 Dispatch Available</span>
        </div>
      </div>

      <Form className="space-y-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <FormField control={control} name="destination" rules={{
            required: "Pickup location is required"
          }} render={({
            field
          }) => <FormItem className="space-y-0">
                  <FormControl>
                    <div className="relative group">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary z-10">
                        <MapPin className="size-5" />
                      </div>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <SelectTrigger className="h-16 w-full border-0 bg-white/90 pl-12 text-left text-base font-medium shadow-soft transition-all hover:bg-white focus:ring-2 focus:ring-primary/40 rounded-lg">
                          <SelectValue placeholder="Select Pickup Area" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lax">LAX International Airport</SelectItem>
                          <SelectItem value="beverly-hills">Beverly Hills</SelectItem>
                          <SelectItem value="santa-monica">Santa Monica</SelectItem>
                          <SelectItem value="burbank">Burbank Airport (BUR)</SelectItem>
                          <SelectItem value="downtown">Downtown Los Angeles</SelectItem>
                          <SelectItem value="malibu">Malibu / Pacific Palisades</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </FormControl>
                  <FormMessage className="absolute text-xs mt-1 font-medium" />
                </FormItem>} />
          </div>

          <div className="lg:col-span-3">
            <FormField control={control} name="check_in" rules={{
            required: "Pickup date is required"
          }} render={({
            field
          }) => <FormItem className="space-y-0">
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button variant="outline" className={cn("h-16 w-full justify-start border-0 bg-white/90 pl-4 text-left font-normal shadow-soft hover:bg-white focus:ring-2 focus:ring-primary/40 rounded-lg", !field.value && "text-muted-foreground")}>
                          <SafeHydrate><CalendarIcon className="mr-3 size-5 text-primary" /></SafeHydrate>
                          <div className="flex flex-col items-start">
                            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest leading-none mb-1">Pickup Date</span>
                            <span className="font-semibold text-foreground">
                              {field.value ? format(new Date(field.value), "MMM dd, yyyy") : "Select Date"}
                            </span>
                          </div>
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date?.toISOString())} disabled={date => date < new Date()} initialFocus /></SafeHydrate>
                    </PopoverContent>
                  </Popover>
                  <FormMessage className="absolute text-xs mt-1 font-medium" />
                </FormItem>} />
          </div>

          <div className="lg:col-span-3">
            <FormField control={control} name="check_out" rules={{
            required: "Return date is required"
          }} render={({
            field
          }) => <FormItem className="space-y-0">
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button variant="outline" className={cn("h-16 w-full justify-start border-0 bg-white/90 pl-4 text-left font-normal shadow-soft hover:bg-white focus:ring-2 focus:ring-primary/40 rounded-lg", !field.value && "text-muted-foreground")}>
                          <SafeHydrate><CalendarIcon className="mr-3 size-5 text-primary" /></SafeHydrate>
                          <div className="flex flex-col items-start">
                            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest leading-none mb-1">Return Date</span>
                            <span className="font-semibold text-foreground">
                              {field.value ? format(new Date(field.value), "MMM dd, yyyy") : "Select Date"}
                            </span>
                          </div>
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date?.toISOString())} disabled={date => date < new Date()} initialFocus /></SafeHydrate>
                    </PopoverContent>
                  </Popover>
                  <FormMessage className="absolute text-xs mt-1 font-medium" />
                </FormItem>} />
          </div>

          <div className="lg:col-span-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-16 w-full justify-start border-0 bg-white/90 pl-4 text-left font-normal shadow-soft hover:bg-white focus:ring-2 focus:ring-primary/40 rounded-lg">
                  <Users className="mr-3 size-5 text-primary" />
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest leading-none mb-1">Guests</span>
                    <span className="font-semibold text-foreground">
                      {totalPassengers} Pax
                    </span>
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-6 rounded-lg shadow-strong" align="end">
                <div className="space-y-4">
                  <h4 className="font-display font-bold text-lg text-foreground">Passengers</h4>
                  <div className="grid gap-1">
                    <FormField control={control} name="adults" render={({
                    field
                  }) => <PassengerStepper label="Adults" value={Number(field.value) || 1} onChange={field.onChange} min={1} />} />
                    <FormField control={control} name="children" render={({
                    field
                  }) => <PassengerStepper label="Children" value={Number(field.value) || 0} onChange={field.onChange} min={0} />} />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem className="space-y-0">
                  <FormControl>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary">
                        <Mail className="size-5" />
                      </div>
                      <Input {...field} type="email" placeholder="Email Address" className="h-14 border-0 bg-white/90 pl-12 shadow-soft hover:bg-white focus-visible:ring-2 focus-visible:ring-primary/40 rounded-lg" />
                    </div>
                  </FormControl>
                  <FormMessage className="absolute text-xs mt-1 font-medium" />
                </FormItem>} />
          </div>

          <div className="lg:col-span-4">
            <FormField control={control} name="phone" rules={{
            required: "Phone number is required"
          }} render={({
            field
          }) => <FormItem className="space-y-0">
                  <FormControl>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary">
                        <Phone className="size-5" />
                      </div>
                      <Input {...field} type="tel" placeholder="Phone Number" className="h-14 border-0 bg-white/90 pl-12 shadow-soft hover:bg-white focus-visible:ring-2 focus-visible:ring-primary/40 rounded-lg" />
                    </div>
                  </FormControl>
                  <FormMessage className="absolute text-xs mt-1 font-medium" />
                </FormItem>} />
          </div>

          <div className="lg:col-span-4">
            <Button type="submit" disabled={isSubmitting} className="btn-primary h-14 w-full shadow-strong">
              {isSubmitting ? "Processing..." : <>
                  <Search className="mr-2 size-5" />
                  Check Availability
                </>}
            </Button>
          </div>
        </div>
      </Form>
    </div>;
}
export default function Booking() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="bookingform" className="relative w-full overflow-hidden bg-background py-20 lg:py-32">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/90 via-primary-dark/70 to-primary-dark/90 z-10" />
        <div className="h-full w-full bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/luxury_chauffeur_la.png)] bg-cover bg-center" />
      </div>

      <div className="container relative z-20 mx-auto px-4">
        <div ref={ref} className={cn("mx-auto max-w-6xl transition-all duration-1000 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0")}>
          <div className="mb-12 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-secondary/20 border border-secondary/30 text-secondary text-xs font-bold uppercase tracking-widest mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-secondary opacity-75"></span>
                <span className="relative inline-flex rounded-lg h-2 w-2 bg-secondary"></span>
              </span>
              Premium Reservations
            </div>
            <h2 className="text-4xl font-display font-bold tracking-tight text-white md:text-5xl lg:text-7xl leading-tight">
              Experience <span className="text-secondary">Elite</span> <br className="hidden md:block" /> Transportation
            </h2>
            <p className="mt-6 text-lg md:text-xl text-white/80 font-medium max-w-2xl">
              Professional chauffeur services for Los Angeles. Punctual, private, and tailored to your schedule.
            </p>
          </div>

          <div className="glass-effect p-8 md:p-10 lg:p-12 rounded-lg border-white/20">
            <FormProvider formKey="booking_form_home" formId="booking_form_home" sectionName="booking-section" defaultValues={{
            destination: "",
            check_in: "",
            check_out: "",
            adults: 1,
            children: 0,
            email: "",
            phone: ""
          }} validationRules={{
            destination: {
              required: true
            },
            check_in: {
              required: true
            },
            check_out: {
              required: true
            },
            adults: {
              min: 1
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            }
          }} submitText="Book Your Ride" successMessage="Your reservation request has been received. Our team will contact you shortly to confirm.">
              <BookingFormContent />
            </FormProvider>
          </div>
          
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-white/10 pt-12">
            {[{
            label: "Professional Drivers",
            value: "Vetted & Uniformed"
          }, {
            label: "Premium Fleet",
            value: "Latest Models"
          }, {
            label: "Punctuality",
            value: "100% Guaranteed"
          }, {
            label: "Support",
            value: "24/7 Concierge"
          }].map((stat, i) => <div key={i} data-index={i} className="text-center md:text-left">
                <p className="text-[10px] text-secondary font-bold uppercase tracking-widest mb-1">{stat.label}</p>
                <p className="text-white font-semibold">{stat.value}</p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}